/**
 * Session Management Hook
 * Provides session management functionality with SOC 2 compliant idle timeout.
 *
 * Idle timeout policy:
 * - Standard sessions: 15 minutes of inactivity
 * - Elevated/admin sessions: 5 minutes of inactivity
 * - Warning shown 2 minutes before forced logout
 * - Activity tracking throttled to once per 30 seconds
 */

'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { forceClientSessionExpiredSignOut } from '@/domains/auth/utils/sign-out';

export interface SessionInfo {
  session_id: string;
  security_level: 'standard' | 'elevated' | 'admin' | 'trading';
  session_type: 'web' | 'mobile' | 'api' | 'desktop';
  created_at: string;
  expires_at: string;
  last_activity_at: string;
  ip_address: string;
  user_agent?: string;
  is_active: boolean;
  mfa_verified: boolean;
}

interface SessionStats {
  total_cached_sessions: number;
  active_cached_sessions: number;
  security_level_distribution: Record<string, number>;
  cleanup_task_running: boolean;
}

/** Idle timeout durations by security level (milliseconds) */
const IDLE_TIMEOUT_BY_LEVEL: Record<string, number> = {
  standard: 15 * 60 * 1000,  // 15 minutes
  elevated: 5 * 60 * 1000,   // 5 minutes
  admin: 5 * 60 * 1000,      // 5 minutes
  trading: 10 * 60 * 1000,   // 10 minutes
};

/** Warning shown this many ms before forced logout */
const IDLE_WARNING_BEFORE_TIMEOUT = 2 * 60 * 1000; // 2 minutes

/** Minimum interval between activity timestamp updates */
const ACTIVITY_THROTTLE_MS = 30 * 1000; // 30 seconds

export interface UseSessionReturn {
  // Session state
  session: SessionInfo | null;
  isLoading: boolean;
  error: string | null;

  // Idle timeout state
  idleWarningVisible: boolean;
  idleSecondsRemaining: number;
  dismissIdleWarning: () => void;

  // Session actions
  createSession: (securityLevel?: string, sessionType?: string, mfaVerified?: boolean) => Promise<void>;
  refreshSession: () => Promise<void>;
  terminateSession: (sessionId?: string, reason?: string) => Promise<void>;
  terminateAllSessions: () => Promise<void>;

  // Session management
  listSessions: () => Promise<SessionInfo[]>;
  getSessionStats: () => Promise<SessionStats | null>;

  // Utility functions
  isSessionExpired: () => boolean;
  getTimeUntilExpiry: () => number; // milliseconds
  logout: () => void;
}

const SESSION_REFRESH_INTERVAL = 4 * 60 * 1000; // 4 minutes
const SESSION_EXPIRY_WARNING = 1 * 60 * 1000; // 1 minute warning

export function useSession(): UseSessionReturn {
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Idle timeout state
  const [idleWarningVisible, setIdleWarningVisible] = useState(false);
  const [idleSecondsRemaining, setIdleSecondsRemaining] = useState(0);

  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const idleCheckRef = useRef<NodeJS.Timeout | null>(null);
  const idleCountdownRef = useRef<NodeJS.Timeout | null>(null);

  // API base URL
  // Same-origin BFF (empty) — never default to localhost:8000 (QTP) for cookie session APIs
  const API_BASE = process.env.NEXT_PUBLIC_API_URL || process.env.NEXT_PUBLIC_API_BASE_URL || '';

  // Get session ID from storage
  const getSessionId = useCallback((): string | null => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('session_id') || sessionStorage.getItem('session_id');
  }, []);

  // Set session ID in storage
  const setSessionId = useCallback((sessionId: string, persistent: boolean = true): void => {
    if (typeof window === 'undefined') return;

    if (persistent) {
      localStorage.setItem('session_id', sessionId);
    } else {
      sessionStorage.setItem('session_id', sessionId);
    }
  }, []);

  // Remove session ID from storage
  const removeSessionId = useCallback((): void => {
    if (typeof window === 'undefined') return;
    localStorage.removeItem('session_id');
    sessionStorage.removeItem('session_id');
  }, []);

  // API request helper
  const apiRequest = useCallback(async (
    endpoint: string,
    options: RequestInit = {}
  ): Promise<any> => {
    const sessionId = getSessionId();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    };

    if (sessionId) {
      headers['Authorization'] = `Bearer ${sessionId}`;
    }

    const response = await fetch(`${API_BASE}/api/v1${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      if (response.status === 401) {
        // Session expired
        removeSessionId();
        setSession(null);
        throw new Error('Session expired');
      }

      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}`);
    }

    return response.json();
  }, [API_BASE, getSessionId, removeSessionId]);

  // Get current session
  const getCurrentSession = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      const sessionId = getSessionId();
      if (!sessionId) {
        setSession(null);
        return;
      }

      const sessionData = await apiRequest('/sessions/current');
      setSession(sessionData);

      // Reset activity timestamp on session load
      lastActivityRef.current = Date.now();

    } catch (err) {
      console.error('Failed to get current session:', err);
      setError(err instanceof Error ? err.message : 'Failed to get session');
      setSession(null);
    } finally {
      setIsLoading(false);
    }
  }, [apiRequest, getSessionId]);

  // Create new session
  const createSession = useCallback(async (
    securityLevel: string = 'standard',
    sessionType: string = 'web',
    mfaVerified: boolean = false
  ): Promise<void> => {
    try {
      setError(null);

      const sessionData = await apiRequest('/sessions/create', {
        method: 'POST',
        body: JSON.stringify({
          security_level: securityLevel,
          session_type: sessionType,
          mfa_verified: mfaVerified,
        }),
      });

      setSession(sessionData);
      setSessionId(sessionData.session_id, true);

    } catch (err) {
      console.error('Failed to create session:', err);
      setError(err instanceof Error ? err.message : 'Failed to create session');
      throw err;
    }
  }, [apiRequest, setSessionId]);

  // Refresh session
  const refreshSession = useCallback(async (): Promise<void> => {
    try {
      setError(null);

      const sessionData = await apiRequest('/sessions/refresh', {
        method: 'POST',
      });

      setSession(sessionData);

    } catch (err) {
      console.error('Failed to refresh session:', err);
      setError(err instanceof Error ? err.message : 'Failed to refresh session');
      throw err;
    }
  }, [apiRequest]);

  // Terminate session
  const terminateSession = useCallback(async (
    sessionId?: string,
    reason: string = 'manual_termination'
  ): Promise<void> => {
    try {
      setError(null);

      await apiRequest('/sessions/terminate', {
        method: 'POST',
        body: JSON.stringify({
          session_id: sessionId,
          reason: reason,
        }),
      });

      // If terminating current session, clear local state
      if (!sessionId || sessionId === session?.session_id) {
        setSession(null);
        removeSessionId();
      }

    } catch (err) {
      console.error('Failed to terminate session:', err);
      setError(err instanceof Error ? err.message : 'Failed to terminate session');
      throw err;
    }
  }, [apiRequest, session?.session_id, removeSessionId]);

  // Terminate all sessions
  const terminateAllSessions = useCallback(async (): Promise<void> => {
    try {
      setError(null);

      await apiRequest('/sessions/terminate-all', {
        method: 'POST',
      });

      setSession(null);
      removeSessionId();

    } catch (err) {
      console.error('Failed to terminate all sessions:', err);
      setError(err instanceof Error ? err.message : 'Failed to terminate all sessions');
      throw err;
    }
  }, [apiRequest, removeSessionId]);

  // List all sessions
  const listSessions = useCallback(async (): Promise<SessionInfo[]> => {
    try {
      const response = await apiRequest('/sessions/list');
      return response.sessions;
    } catch (err) {
      console.error('Failed to list sessions:', err);
      throw err;
    }
  }, [apiRequest]);

  // Get session stats (admin only)
  const getSessionStats = useCallback(async (): Promise<SessionStats | null> => {
    try {
      return await apiRequest('/sessions/stats');
    } catch (err) {
      console.error('Failed to get session stats:', err);
      return null;
    }
  }, [apiRequest]);

  // Check if session is expired
  const isSessionExpired = useCallback((): boolean => {
    if (!session) return true;
    return new Date(session.expires_at).getTime() <= Date.now();
  }, [session]);

  // Get time until session expires
  const getTimeUntilExpiry = useCallback((): number => {
    if (!session) return 0;
    return Math.max(0, new Date(session.expires_at).getTime() - Date.now());
  }, [session]);

  // Logout function
  const logout = useCallback((): void => {
    setSession(null);
    removeSessionId();
    forceClientSessionExpiredSignOut({
      error: 'session_expired',
      skipIfPublic: false,
    });
  }, [removeSessionId]);

  // Dismiss idle warning and refresh session (user clicked "Continue Working")
  const dismissIdleWarning = useCallback(() => {
    setIdleWarningVisible(false);
    setIdleSecondsRemaining(0);
    lastActivityRef.current = Date.now();
    if (idleCountdownRef.current) {
      clearInterval(idleCountdownRef.current);
      idleCountdownRef.current = null;
    }
    // Refresh the session since user is active
    refreshSession().catch(() => { });
  }, [refreshSession]);

  // Set up automatic session refresh (only refreshes when user has been active)
  useEffect(() => {
    if (session && session.is_active) {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }

      // Refresh session only if there has been recent activity
      refreshIntervalRef.current = setInterval(() => {
        const idleDuration = Date.now() - lastActivityRef.current;
        if (idleDuration < SESSION_REFRESH_INTERVAL) {
          refreshSession().catch(() => { });
        }
      }, SESSION_REFRESH_INTERVAL);

      // Set up token expiry warning
      const timeUntilExpiry = getTimeUntilExpiry();
      if (timeUntilExpiry > SESSION_EXPIRY_WARNING) {
        warningTimeoutRef.current = setTimeout(() => {
          // Token expiry is separate from idle timeout - both can trigger warnings
        }, timeUntilExpiry - SESSION_EXPIRY_WARNING);
      }
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
    };
  }, [session, refreshSession, getTimeUntilExpiry]);

  // Idle timeout checker - runs every 10 seconds to check inactivity
  useEffect(() => {
    if (!session || !session.is_active) return;

    const securityLevel = session.security_level || 'standard';
    const idleTimeoutMs = IDLE_TIMEOUT_BY_LEVEL[securityLevel] ?? IDLE_TIMEOUT_BY_LEVEL.standard ?? 15 * 60 * 1000;
    const warningAtMs = idleTimeoutMs - IDLE_WARNING_BEFORE_TIMEOUT;

    idleCheckRef.current = setInterval(() => {
      const idleDuration = Date.now() - lastActivityRef.current;

      if (idleDuration >= idleTimeoutMs) {
        // Idle timeout reached - force logout
        setIdleWarningVisible(false);
        if (idleCountdownRef.current) {
          clearInterval(idleCountdownRef.current);
          idleCountdownRef.current = null;
        }
        logout();
        return;
      }

      if (idleDuration >= warningAtMs && !idleWarningVisible) {
        // Show warning and start countdown
        setIdleWarningVisible(true);
        const remainingMs = idleTimeoutMs - idleDuration;
        setIdleSecondsRemaining(Math.ceil(remainingMs / 1000));

        // Start 1-second countdown
        if (idleCountdownRef.current) {
          clearInterval(idleCountdownRef.current);
        }
        idleCountdownRef.current = setInterval(() => {
          const currentIdle = Date.now() - lastActivityRef.current;
          const remaining = Math.max(0, idleTimeoutMs - currentIdle);
          setIdleSecondsRemaining(Math.ceil(remaining / 1000));

          if (remaining <= 0) {
            logout();
          }
        }, 1000);
      }
    }, 10_000); // Check every 10 seconds

    return () => {
      if (idleCheckRef.current) {
        clearInterval(idleCheckRef.current);
      }
      if (idleCountdownRef.current) {
        clearInterval(idleCountdownRef.current);
      }
    };
  }, [session, idleWarningVisible, logout]);

  // Initialize session on mount
  useEffect(() => {
    getCurrentSession();
  }, [getCurrentSession]);

  // Activity tracking - throttled to once per 30 seconds
  useEffect(() => {
    let throttleTimeout: NodeJS.Timeout | null = null;
    let isThrottled = false;

    const handleActivity = () => {
      if (isThrottled) return;
      if (!session || !session.is_active) return;

      lastActivityRef.current = Date.now();

      // If warning is visible and user interacts, dismiss it
      if (idleWarningVisible) {
        dismissIdleWarning();
      }

      isThrottled = true;
      throttleTimeout = setTimeout(() => {
        isThrottled = false;
      }, ACTIVITY_THROTTLE_MS);
    };

    const events = ['mousedown', 'keypress', 'scroll', 'touchstart', 'click'];

    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
    };
  }, [session, idleWarningVisible, dismissIdleWarning]);

  return {
    // Session state
    session,
    isLoading,
    error,

    // Idle timeout state
    idleWarningVisible,
    idleSecondsRemaining,
    dismissIdleWarning,

    // Session actions
    createSession,
    refreshSession,
    terminateSession,
    terminateAllSessions,

    // Session management
    listSessions,
    getSessionStats,

    // Utility functions
    isSessionExpired,
    getTimeUntilExpiry,
    logout,
  };
}
